import { IMAGE_FILE_TYPE, PDF_FILE_TYPE } from '@/platform/lib/utils';
import {
    Button,
    Checkbox,
    Container,
    Dropdown,
    Form,
    FormGroup,
    Input,
    InputPlaceholder,
    Segment,
    SegmentContent,
    SegmentHeader,
} from '@/platform/ui';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Deferred, Link, useForm, usePage } from '@inertiajs/react';
import { useEffect, useMemo } from 'react';

const CreateOrUpdateView = () => {
    const { reseller, costunits } = usePage().props;
    const { data, setData, post, transform, errors, processing } = useForm({});

    useEffect(() => {
        if (reseller?.id) {
            setData(reseller);
        }
    }, [reseller?.id]);

    const title = useMemo(
        () => (reseller?.id ? 'Wijzig reseller' : 'Nieuw reseller'),
        [reseller]
    );

    const handleSubmit = () => {
        if (reseller?.id) {
            // Files cannot be sent over a real PATCH, so spoof the method on a
            // POST while keeping the form helper's errors and processing state.
            transform(data => ({ ...data, _method: 'patch' }));

            post(`/resellers/${reseller.id}`);

            return;
        }

        post('/resellers');
    };

    return (
        <Container>
            <Form onSubmit={handleSubmit}>
                <Segment>
                    <SegmentHeader header={title}>
                        <Button as={Link} href='/resellers'>
                            <ArrowLeftIcon className='size-5' />
                            Terug
                        </Button>
                    </SegmentHeader>
                    <SegmentContent>
                        <FormGroup>
                            <Input
                                label='Naam'
                                required
                                value={data?.name || ''}
                                onChange={event =>
                                    setData('name', event.target.value)
                                }
                                error={errors?.name}
                            />
                            <Deferred
                                data={['costunits']}
                                fallback={
                                    <InputPlaceholder label='Exact Online kostendrager' />
                                }
                            >
                                <Dropdown
                                    label='Exact Online kostendrager'
                                    value={data?.exact_online_costunit || ''}
                                    options={costunits?.map(unit => ({
                                        id: unit.code,
                                        name: unit.description,
                                    }))}
                                    onChange={value =>
                                        setData('exact_online_costunit', value)
                                    }
                                    error={errors?.exact_online_costunit}
                                />
                            </Deferred>
                            <Input
                                label='Domein'
                                required
                                value={data?.domain || ''}
                                onChange={event =>
                                    setData('domain', event.target.value)
                                }
                                error={errors?.domain}
                            />
                            <Input
                                label='Afzender email prefix'
                                suffix='@vcsw.nl'
                                required
                                value={data?.email_prefix || ''}
                                onChange={event =>
                                    setData('email_prefix', event.target.value)
                                }
                                error={errors?.email_prefix}
                            />
                        </FormGroup>
                        <FormGroup equal inline>
                            <Input
                                label='E-mailadres'
                                type='email'
                                required
                                value={data?.email || ''}
                                onChange={event =>
                                    setData('email', event.target.value)
                                }
                                error={errors?.email}
                            />
                            <Input
                                label='Telefoonnummer'
                                required
                                value={data?.telephone_number || ''}
                                onChange={event =>
                                    setData(
                                        'telephone_number',
                                        event.target.value
                                    )
                                }
                                error={errors?.telephone_number}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Input
                                label='E-mailadres voor ontvangst DGV'
                                type='email'
                                value={data?.email_dgv || ''}
                                onChange={event =>
                                    setData('email_dgv', event.target.value)
                                }
                                error={errors?.email_dgv}
                            />
                        </FormGroup>
                        <FormGroup equal inline>
                            <Input
                                label='Primaire kleur'
                                type='color'
                                value={data?.color || '#000000'}
                                onChange={event =>
                                    setData('color', event.target.value)
                                }
                                error={errors?.color}
                            />
                            <Input
                                label='Dashboard kleuren'
                                value={data?.dashboard_colors || ''}
                                onChange={event =>
                                    setData(
                                        'dashboard_colors',
                                        event.target.value
                                    )
                                }
                                error={errors?.dashboard_colors}
                            />
                        </FormGroup>
                        <FormGroup inline equal>
                            <Input
                                label='Logo'
                                type='file'
                                accept={IMAGE_FILE_TYPE}
                                onChange={event =>
                                    setData('logo', event.target.files[0])
                                }
                                error={errors?.logo}
                            />
                            <Input
                                label='Favicon'
                                type='file'
                                accept={IMAGE_FILE_TYPE}
                                onChange={event =>
                                    setData('favicon', event.target.files[0])
                                }
                                error={errors?.favicon}
                            />
                            <Input
                                label='Privacy Statement'
                                type='file'
                                accept={PDF_FILE_TYPE}
                                onChange={event =>
                                    setData(
                                        'privacy_statement',
                                        event.target.files[0]
                                    )
                                }
                                error={errors?.privacy_statement}
                            />
                        </FormGroup>
                    </SegmentContent>
                </Segment>

                <Segment>
                    <SegmentHeader header='Facturatie' />
                    <SegmentContent>
                        <FormGroup>
                            <Checkbox
                                label='Facturatie naar reseller'
                                checked={!!data?.force_invoice || false}
                                onChange={checked =>
                                    setData('force_invoice', checked)
                                }
                                error={errors?.force_invoice}
                            />
                        </FormGroup>
                        <FormGroup inline equal>
                            <Input
                                label='Straat'
                                value={data?.street || ''}
                                onChange={event =>
                                    setData('street', event.target.value)
                                }
                            />
                            <Input
                                label='Huisnummer'
                                value={data?.house_number || ''}
                                onChange={event =>
                                    setData('house_number', event.target.value)
                                }
                            />
                        </FormGroup>
                        <FormGroup inline equal>
                            <Input
                                label='Postcode'
                                value={data?.zipcode || ''}
                                onChange={event =>
                                    setData('zipcode', event.target.value)
                                }
                            />
                            <Input
                                label='Plaats'
                                value={data?.city || ''}
                                onChange={event =>
                                    setData('city', event.target.value)
                                }
                            />
                        </FormGroup>

                        <FormGroup>
                            <Input
                                label='Factuur e-mailadres'
                                type='email'
                                value={data?.invoice_email || ''}
                                onChange={event =>
                                    setData('invoice_email', event.target.value)
                                }
                            />

                            <Input
                                label='Factuur BCC'
                                name='invoice_bcc'
                                value={data?.invoice_bcc || ''}
                                onChange={event =>
                                    setData('invoice_bcc', event.target.value)
                                }
                            />

                            <Input
                                label='PO nummer'
                                value={data?.po_number || ''}
                                onChange={event =>
                                    setData('po_number', event.target.value)
                                }
                            />

                            <Input
                                label='T.a.v.'
                                value={data?.ascription || ''}
                                onChange={event =>
                                    setData('ascription', event.target.value)
                                }
                            />
                        </FormGroup>
                    </SegmentContent>
                </Segment>

                <Segment floating>
                    <SegmentContent right>
                        <Button
                            type='submit'
                            variant='primary'
                            loading={processing}
                        >
                            Opslaan
                        </Button>
                    </SegmentContent>
                </Segment>
            </Form>
        </Container>
    );
};

export default CreateOrUpdateView;
